import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import StatusBadge from '../components/StatusBadge'
import './Dashboard.css'

export default function Dashboard() {
  const { user, profile, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [projects, setProjects] = useState([])
  const [enquiries, setEnquiries] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (authLoading) return
    if (!user) { navigate('/login'); return }
    fetchData()
  }, [user, authLoading, location.key]) // eslint-disable-line

  async function fetchData() {
    if (!user) return
    setLoading(true)
    try {
      const [projRes, enqRes] = await Promise.all([
        supabase.from('projects').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('enquiries').select('*').eq('creator_id', user.id).order('created_at', { ascending: false }).limit(10),
      ])
      setProjects(projRes.data || [])
      setEnquiries(enqRes.data || [])
    } catch (err) {
      console.error('Dashboard fetch failed:', err)
    } finally {
      setLoading(false)
    }
  }

  async function markEnquiryRead(id) {
    await supabase.from('enquiries').update({ read: true }).eq('id', id)
    setEnquiries(enqs => enqs.map(e => e.id === id ? { ...e, read: true } : e))
  }

  const verified = projects.filter(p => p.status === 'verified').length
  const inProgress = projects.filter(p => ['ongoing', 'submitted', 'needs_evidence'].includes(p.status)).length
  const unreadEnquiries = enquiries.filter(e => !e.read).length
  const reviewsCompleted = profile?.reviews_completed_count || 0

  if (authLoading) return <div style={{ textAlign: 'center', padding: 80 }}><span className="loading-spinner" /></div>

  return (
    <div className="dashboard-page page-fade">
      <div className="container">
        <div className="dash-header">
          <div>
            <h1>Dashboard</h1>
            <p className="dash-sub">Welcome back, {profile?.display_name || profile?.full_name || 'creator'}</p>
          </div>
          <Link to="/project/new" className="btn btn-primary">+ New project</Link>
        </div>

        {/* Quick links */}
        {(profile?.username) && (
          <div className="dash-quicklinks">
            <Link to={`/u/${profile.username}`} className="btn btn-secondary btn-sm">View my profile →</Link>
            <Link to="/settings/profile" className="btn btn-ghost btn-sm">Edit profile</Link>
          </div>
        )}

        {/* Reviewer progress — the cold-start gate: review 2 to unlock submitting */}
        <div className="quota-card card">
          <div className="quota-header">
            <div>
              <span className="quota-label">Reviews completed</span>
              <span className="quota-reset">Review 2 projects to unlock submitting your own</span>
            </div>
            <span className="quota-count">
              {Math.min(reviewsCompleted, 2)} / 2
            </span>
          </div>
          <div className="quota-bar-wrap">
            <div className="quota-bar-fill" style={{ width: `${Math.min((reviewsCompleted / 2) * 100, 100)}%`, background: 'var(--accent)' }} />
          </div>
          {reviewsCompleted < 2 && (
            <p className="quota-full-msg"><Link to="/review">Head to the review queue →</Link></p>
          )}
        </div>

        {/* Stats */}
        <div className="dash-stats">
          <div className="stat-card"><span className="stat-label">Verified works</span><span className="stat-value" style={{ color: 'var(--accent)' }}>{verified}</span></div>
          <div className="stat-card"><span className="stat-label">In progress</span><span className="stat-value" style={{ color: 'var(--ongoing)' }}>{inProgress}</span></div>
          <div className="stat-card"><span className="stat-label">Total projects</span><span className="stat-value">{projects.length}</span></div>
          <div className="stat-card">
            <span className="stat-label">Enquiries {unreadEnquiries > 0 && <span className="enquiry-dot">{unreadEnquiries}</span>}</span>
            <span className="stat-value" style={{ color: unreadEnquiries > 0 ? 'var(--pending)' : 'var(--text)' }}>{enquiries.length}</span>
          </div>
        </div>

        <div className="dash-body">
          <div className="dash-main">
            <div className="dash-section-header">
              <h2>Your projects</h2>
              <Link to="/project/new" className="btn btn-ghost btn-sm">+ New</Link>
            </div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><span className="loading-spinner" /></div>
            ) : projects.length === 0 ? (
              <div className="empty-state card">
                <div style={{ fontSize: 36, marginBottom: 12 }}>🎨</div>
                <h3>No projects yet</h3>
                <p>Start documenting your creative process and get verified as a human creator.</p>
                <Link to="/project/new" className="btn btn-primary" style={{ marginTop: 16 }}>Start your first project →</Link>
              </div>
            ) : (
              <div className="projects-list">
                {projects.map(p => <DashProjectRow key={p.id} project={p} />)}
              </div>
            )}
          </div>

          <div className="dash-sidebar">
            {/* Enquiries — unlocked once you have at least one verified project */}
            {verified > 0 ? (
              <div className="card">
                <h3 style={{ marginBottom: 12 }}>
                  Enquiries {unreadEnquiries > 0 && <span className="enquiry-dot">{unreadEnquiries} new</span>}
                </h3>
                {enquiries.length === 0 ? (
                  <p style={{ color: 'var(--text-3)', fontSize: 13 }}>No enquiries yet. Visitors can contact you from your public profile.</p>
                ) : (
                  <div className="enquiries-list">
                    {enquiries.map(e => (
                      <div key={e.id} className={`enquiry-item ${!e.read ? 'unread' : ''}`} onClick={() => markEnquiryRead(e.id)}>
                        <div className="enquiry-from">{e.sender_name} {!e.read && <span className="enquiry-new-dot" />}</div>
                        <div className="enquiry-email">{e.sender_email}</div>
                        <div className="enquiry-msg">{e.message.slice(0, 100)}{e.message.length > 100 ? '…' : ''}</div>
                        <div className="enquiry-date">{new Date(e.created_at).toLocaleDateString()}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="card coming-soon-card">
                <h4>Contact form</h4>
                <p>Get your first project verified to let visitors send you enquiries from your profile.</p>
                <span className="coming-soon-flag" style={{ marginTop: 8, display: 'inline-flex' }}>Unlocks after verification</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function DashProjectRow({ project }) {
  const visIcon = { public: '🌐', unlisted: '🔗', private: '🔒' }
  return (
    <Link to={`/project/${project.id}`} className="dash-project-row card">
      <div className="dpr-left">
        <div className="dpr-title">{project.title}</div>
        <div className="dpr-meta">
          <span>{project.category === 'Other (describe below)' ? 'Other' : project.category}{project.custom_category ? ` / ${project.custom_category}` : ''}</span>
          <span>·</span>
          <span>{visIcon[project.visibility]} {project.visibility}</span>
          <span>·</span>
          <span>{new Date(project.created_at).toLocaleDateString()}</span>
        </div>
      </div>
      <StatusBadge status={project.status} />
    </Link>
  )
}
